import { createPortal } from 'react-dom';
import type { MutableRefObject } from 'react';
import produce from 'immer';
import EndTimeForeverPicker from './EndTimeForeverPicker';
import TimeGroup from './TimeGroup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'lib/dayjs';

type TypeDayPickerImplProps = {
  hookRef: MutableRefObject<null>;
  value?: DayValue;
  onChange?: (value: DayValue) => void;
};

type DayValue = {
  dtRange:
    | {
        startAt: string | undefined;
        endAt: string | undefined;
      }
    | undefined;
  dayRepeat:
    | {
        startAt: string;
        endAt: string;
      }[]
    | undefined;
};

function defaultValue(): DayValue {
  return {
    dtRange: {
      startAt: undefined,
      endAt: undefined,
    },
    dayRepeat: [],
  };
}

function TypeDayPickerImpl(props: TypeDayPickerImplProps) {
  const { hookRef, value, onChange } = props;
  const [innerValue, setInnerValue] = useState(value || defaultValue());

  useEffect(() => {
    onChange?.(innerValue);
  }, [innerValue, onChange]);
  const handleRangeChange = useCallback((value) => {
    setInnerValue(
      produce((draft) => {
        if (!value[0]) {
          draft.dtRange = undefined;
        } else {
          draft.dtRange = {
            startAt: value[0] || '',
            endAt: value[1] || '',
          };
        }
      }),
    );
  }, []);
  const handleDaysChange = useCallback((value) => {
    setInnerValue(
      produce((draft) => {
        draft.dayRepeat = value;
      }),
    );
  }, []);

  const dtRangeDefaultValue = useMemo(() => {
    const tryValue = innerValue?.dtRange;
    if (tryValue === undefined) {
      return undefined;
    } else {
      try {
        const range = [];
        if (tryValue.startAt) {
          range.push(dayjs(tryValue.startAt, 'YYYY-MM-DD'));
          if (tryValue.endAt) {
            range.push(dayjs(tryValue.endAt, 'YYYY-MM-DD'));
          }
        } else {
          return null;
        }
        return range as [Dayjs, Dayjs];
      } catch (error) {
        return null;
      }
    }
  }, [innerValue]);

  if (!hookRef.current) {
    return null;
  }

  return (
    <>
      <EndTimeForeverPicker
        canNotSelectEnd
        noForever
        value={dtRangeDefaultValue}
        onChange={handleRangeChange}
      />
      {createPortal(
        <TimeGroup value={innerValue.dayRepeat} onChange={handleDaysChange} />,
        hookRef.current!,
      )}
    </>
  );
}

export default TypeDayPickerImpl;
